import type { AxiosInstance, AxiosResponse } from 'axios';

// ---------------------------------------------------------------------------
// Skills browser + override types
// ---------------------------------------------------------------------------

export interface SkillBranch {
  name: string;
  sha: string;
}

export interface BranchListResponse {
  branches: SkillBranch[];
  totalCount: number;
}

export interface SkillCatalogEntry {
  name: string;
  description: string;
}

export interface SkillCatalogResponse {
  ref: string;
  sha: string;
  skills: SkillCatalogEntry[];
  totalCount: number;
}

export interface SkillDetailResponse {
  name: string;
  description: string;
  body: string;
  ref: string;
  sha: string;
}

export interface FileDiffEntry {
  filename: string;
  status: string;
  additions: number;
  deletions: number;
  patch?: string;
}

export interface SkillDiffResponse {
  head: string;
  base: string;
  files: FileDiffEntry[];
  totalChanged: number;
}

export interface SkillSourceResponse {
  id: string;
  ref: string;
  setBy: string;
  setByName: string;
  setAt: string;
  updatedAt: string;
}

export interface DashboardMetrics {
  totalTraces: number;
  activeUsers: number;
  totalSpans: number;
  avgResponseTimeMs: number | null;
  positiveFeedback: number;
  negativeFeedback: number;
  totalScorers: number;
  avgScore: number | null;
}

export interface AgentMetric {
  agent: string;
  conversations: number;
  totalSpans: number;
  avgDurationSeconds: number;
  errorCount: number;
  uniqueUsers: number;
}

export interface AgentAnalyticsResponse {
  items: AgentMetric[];
}

export interface ToolUsageItem {
  toolName: string;
  callCount: number;
  traceCount: number;
  pctOfToolCalls: number;
}

export interface SpanTypeUsageItem {
  spanType: string;
  count: number;
  pctOfSpans: number;
}

export interface AgentToolUsageItem {
  agentName: string;
  toolName: string;
  callCount: number;
  pctOfToolCalls: number;
}

export interface ToolUsageAnalyticsResponse {
  totalSpans: number;
  totalToolCalls: number;
  toolCallSharePct: number;
  uniqueTools: number;
  /**
   * Aggregate count of skill-tool calls (`get-relevant-skills`, `read-skill`,
   * etc.) within the analytics window. Optional for backward compatibility
   * with API revisions that pre-date the field.
   */
  skillToolCallCount?: number;
  /**
   * `skillToolCallCount / totalToolCalls × 100`. Surfaces how often the
   * agent loads a skill before answering — a leading indicator of quality.
   */
  skillToolCallSharePct?: number;
  availableFilters: {
    agentNames: string[];
    entityTypes: string[];
    scopes: string[];
  };
  topTools: ToolUsageItem[];
  spanTypeDistribution: SpanTypeUsageItem[];
  topAgentToolPairs: AgentToolUsageItem[];
}

export interface GetToolUsageParams {
  timeRange?: string;
  agentName?: string;
  entityType?: string;
  scope?: string;
}

export interface TraceListItem {
  traceId: string;
  threadId: string | null;
  conversationId: string | null;
  resourceId: string | null;
  spanId: string;
  name: string;
  spanType: string;
  startedAt: string;
  endedAt: string | null;
  spanCount: number;
  errorCount: number;
  userId: string | null;
  userName: string | null;
  userEmail: string | null;
  hasError: boolean;
  scope: string | null;
  entityType: string | null;
  entityName: string | null;
  conversationTraceCount: number;
  input: string | null;
  output: string | null;
}

export interface TracesResponse {
  items: TraceListItem[];
  totalCount: number;
  page: number;
  perPage: number;
}

/** Compact span shape returned by the trace detail list endpoint. */
export interface SpanSummary {
  spanId: string;
  traceId: string;
  name: string;
  spanType: string;
  parentSpanId: string | null;
  startedAt: string;
  endedAt: string | null;
  userId: string | null;
  threadId: string | null;
  conversationId: string | null;
  error: unknown;
}

/** Full span shape including large payload fields, returned by the individual span endpoint. */
export interface SpanDetail extends SpanSummary {
  input: unknown;
  output: unknown;
  metadata: unknown;
}

/** Skill source metadata attached to a trace when any skill was loaded during it. */
export interface TraceSkillSource {
  type: 'local' | 'github';
  /** Git ref (branch/tag) — only present for GitHub-backed sources. */
  ref?: string;
  /** Resolved commit SHA at load time. Present for both GitHub and local (when image SHA is known). */
  sha?: string;
  /** Repository in owner/repo format — only present for GitHub-backed sources. */
  repo?: string;
  /** Names of skills that were loaded or injected into the agent context. */
  skillsLoaded: string[];
}

export interface ConversationTraceSummary {
  traceId: string;
  name: string;
  spanType: string;
  startedAt: string;
  endedAt: string | null;
  spanCount: number;
  errorCount: number;
  hasError: boolean;
  isCurrentTrace: boolean;
}

export interface TraceConversationSummary {
  conversationId: string;
  threadId: string;
  totalTraceCount: number;
  truncated: boolean;
  traces: ConversationTraceSummary[];
}

export interface TraceDetailResponse {
  traceId: string;
  threadId: string | null;
  conversationId: string | null;
  resourceId: string | null;
  conversation: TraceConversationSummary | null;
  spans: SpanSummary[];
  totalSpans: number;
  truncated: boolean;
  name: string | null;
  spanType: string | null;
  startedAt: string | null;
  endedAt: string | null;
  userName: string | null;
  userEmail: string | null;
  scope: string | null;
  hasError: boolean;
  errorCount: number;
  /** Skill source metadata — present when any skill was accessed during this trace. */
  skillSource: TraceSkillSource | null;
}

export interface ScorerResult {
  id: string;
  scorerId: string | null;
  traceId: string | null;
  spanId: string | null;
  score: number | null;
  reason: string | null;
  input: unknown;
  output: unknown;
  createdAt: string | null;
}

export interface ScorerResultsResponse {
  items: ScorerResult[];
}

export interface ScorerSummary {
  scorerId: string;
  evalCount: number;
  avgScore: number | null;
  minScore: number | null;
  maxScore: number | null;
  agentName: string | null;
  /** Importance weight from the scorer registry (defaults to 1). */
  weight?: number;
  previousAvgScore?: number | null;
  scorerType?: 'llm' | 'deterministic';
}

export interface ScorerSummaryResponse {
  items: ScorerSummary[];
}

export interface ScorerTimeSeriesPoint {
  scorerId: string;
  evalCount: number;
  avgScore: number | null;
}

export interface ScorerTimeSeriesBucket {
  date: string;
  scorers: ScorerTimeSeriesPoint[];
}

export interface ScorerTimeSeriesResponse {
  buckets: ScorerTimeSeriesBucket[];
}

export interface ScorerResultListResponse {
  items: ScorerResult[];
  totalCount: number;
  page: number;
  perPage: number;
}

export interface ScorerCalibrationItem {
  scorerId: string;
  sampleSize: number;
  meanAbsError: number;
  spearman: number | null;
  biasMean: number | null;
  lastRunAt: string;
  trustLevel: 'green' | 'amber' | 'red';
}

export interface ScorerCalibrationResponse {
  items: ScorerCalibrationItem[];
}

export interface ScorerCorrelationPair {
  scorerA: string;
  scorerB: string;
  pairCount: number;
  correlation: number;
}

export interface ScorerCorrelationResponse {
  pairs: ScorerCorrelationPair[];
}

export interface OrgUser {
  id: string;
  auth0_sub?: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface OrgUsersResponse {
  items: OrgUser[];
}
export interface GetTracesParams {
  page?: number;
  perPage?: number;
  userId?: string;
  spanType?: string;
  search?: string;
  scope?: string;
  entityType?: string;
  threadId?: string;
  timeRange?: string;
}

export interface GetScorerResultsParams {
  page?: number;
  perPage?: number;
  scorerId?: string;
  agentName?: string;
  timeRange?: string;
}

export interface AiUserSummary {
  userId: string;
  userName: string | null;
  userEmail: string | null;
  totalTraces: number;
  totalSpans: number;
  avgResponseTimeMs: number | null;
  errorCount: number;
  lastActiveAt: string | null;
}

export interface AiUserSummaryResponse {
  items: AiUserSummary[];
  totalCount: number;
  page: number;
  perPage: number;
}

export interface AiUserDetail {
  userId: string;
  userName: string | null;
  userEmail: string | null;
  totalTraces: number;
  totalSpans: number;
  avgResponseTimeMs: number | null;
  errorCount: number;
  lastActiveAt: string | null;
  agentBreakdown: { agent: string; traceCount: number }[];
  topQuestions: { question: string; askedAt: string }[];
}

export interface UsageDataPoint {
  date: string;
  traces: number;
  spans: number;
  errors: number;
}

export interface UsageTimeSeriesResponse {
  items: UsageDataPoint[];
}

export interface ExportRow {
  traceId: string;
  threadId: string | null;
  userId: string | null;
  userEmail: string | null;
  userName: string | null;
  agent: string;
  startedAt: string;
  endedAt: string | null;
  durationMs: number | null;
  question: string | null;
  answer: string | null;
  hasError: boolean;
}

export interface ExportDataResponse {
  items: ExportRow[];
}

export interface GetAiUsersParams {
  page?: number;
  perPage?: number;
  timeRange?: string;
  search?: string;
}

// ---------------------------------------------------------------------------
// Copilot Performance Analytics types
// ---------------------------------------------------------------------------

export interface CopilotPerformancePreviousPeriod {
  totalTurns: number;
  totalThreads: number;
  avgResponseTimeMs: number | null;
  p50ResponseTimeMs: number | null;
  p95ResponseTimeMs: number | null;
  errorRatePct: number | null;
  negativeFeedbackRatePct: number | null;
}

export interface CopilotPerformanceTargets {
  avgResponseTimeMs: number;
  p95ResponseTimeMs: number;
  errorRatePct: number;
  negativeFeedbackRatePct: number;
}

export interface CopilotPerformanceMetrics {
  totalTurns: number;
  totalThreads: number;
  avgResponseTimeMs: number | null;
  p50ResponseTimeMs: number | null;
  p75ResponseTimeMs: number | null;
  p95ResponseTimeMs: number | null;
  errorRatePct: number | null;
  avgTurnsPerThread: number | null;
  p50TurnsPerThread: number | null;
  avgThreadDurationMs: number | null;
  p50ThreadDurationMs: number | null;
  positiveFeedbackCount: number;
  negativeFeedbackCount: number;
  negativeFeedbackRatePct: number | null;
  previousPeriod: CopilotPerformancePreviousPeriod | null;
  targets: CopilotPerformanceTargets;
}

export interface CopilotPerformanceDataPoint {
  bucket: string;
  turns: number;
  threads: number;
  avgResponseTimeMs: number | null;
  p95ResponseTimeMs: number | null;
  negativeFeedbackCount: number;
  avgTurnsPerThread: number | null;
  avgThreadDurationMs: number | null;
  errorRatePct: number | null;
}

export interface CopilotPerformanceTimeSeriesResponse {
  items: CopilotPerformanceDataPoint[];
  previousPeriodItems: CopilotPerformanceDataPoint[];
}

export interface CopilotPerformanceByScopeItem {
  scope: string;
  turns: number;
  avgResponseTimeMs: number | null;
  p50ResponseTimeMs: number | null;
  p95ResponseTimeMs: number | null;
  errorRatePct: number | null;
  avgToolCallsPerTurn: number | null;
}

export interface CopilotPerformanceByScopeResponse {
  items: CopilotPerformanceByScopeItem[];
}

export interface CopilotPerformanceStepItem {
  category: string;
  spanCount: number;
  avgDurationMs: number | null;
  p50DurationMs: number | null;
  p95DurationMs: number | null;
  sharePct: number | null;
  prevAvgDurationMs: number | null;
}

export interface CopilotPerformanceStepsResponse {
  items: CopilotPerformanceStepItem[];
}

export interface CopilotSlowDbQuery {
  spanId: string;
  traceId: string;
  query: string | null;
  table: string | null;
  path: string | null;
  dataProduct: string | null;
  rowCount: number | null;
  isHealRetry: boolean;
  hasError: boolean;
  durationMs: number;
  startedAt: string;
}

export interface CopilotDbQueriesResponse {
  totalQueries: number;
  avgDurationMs: number | null;
  p95DurationMs: number | null;
  errorCount: number;
  healRetryCount: number;
  slowest: CopilotSlowDbQuery[];
}

export interface ChatFeedbackListItem {
  id: string;
  createdAt: string;
  feedback: string;
  userId: string;
  userName: string | null;
  userEmail: string | null;
  threadId: string;
  messageId: string;
  traceId: string | null;
  runId: string | null;
  comment: string | null;
}

export interface ChatFeedbackDetail extends ChatFeedbackListItem {
  question?: string;
  answer?: string;
}

export interface ChatFeedbackListResponse {
  items: ChatFeedbackListItem[];
  totalCount: number;
  page: number;
  perPage: number;
}

export interface GetFeedbackParams {
  page?: number;
  perPage?: number;
  userId?: string;
  feedback?: 'POSITIVE' | 'NEGATIVE';
}

export interface McpFeedbackListItem {
  id: string;
  createdAt: string;
  feedback: string;
  userId: string;
  userName: string | null;
  userEmail: string | null;
}

export interface McpFeedbackListResponse {
  items: McpFeedbackListItem[];
  totalCount: number;
  page: number;
  perPage: number;
}

export interface GetMcpFeedbackParams {
  page?: number;
  perPage?: number;
}

export interface UsageSummary {
  totalInputTokens: number;
  totalOutputTokens: number;
  totalTokens: number;
  estimatedCostUsd: number;
  traceCount: number;
}

export interface UsageByAgent {
  agentId: string;
  agentName: string | null;
  totalInputTokens: number;
  totalOutputTokens: number;
  estimatedCostUsd: number;
  traceCount: number;
}

export interface UsageByUser {
  userId: string;
  totalInputTokens: number;
  totalOutputTokens: number;
  estimatedCostUsd: number;
  traceCount: number;
}

export interface CostTimeSeries {
  date: string;
  inputTokens: number;
  outputTokens: number;
  estimatedCostUsd: number;
  traceCount: number;
}

export interface CostBreakdownResponse {
  summary: UsageSummary;
  byAgent: UsageByAgent[];
  byUser: UsageByUser[];
  timeSeries: CostTimeSeries[];
}

/** Hit/miss/latency counters for one cache domain (e.g. the Mastra tool domain). */
export interface CacheDomainMetrics {
  domain: string;
  hits: number;
  misses: number;
  sets: number;
  errors: number;
  /** Fraction of reads served from cache; 0 when no reads have occurred yet. */
  hitRate: number;
  /** Mean GET latency in milliseconds. */
  avgGetLatencyMs: number;
}

/** Configured TTL policy for a single cacheable tool. */
export interface CacheToolPolicy {
  toolId: string;
  /** Partitioning scope: global | org | user | thread | user-project */
  scope: string;
  ttl: number;
  bypass: boolean;
}

/** Response from GET /admin/analytics/cache. */
export interface CacheMetrics {
  enabled: boolean;
  connected: boolean;
  keyCount: number | null;
  domains: CacheDomainMetrics[];
  policies: CacheToolPolicy[];
}

/** Response from DELETE /admin/analytics/cache. */
export interface CacheClearResult {
  cleared: boolean;
  orgScopedDeleted: number;
  userScopedDeleted: number;
  totalDeleted: number;
}

export class AdminApi {
  constructor(private readonly axios: AxiosInstance) {}

  getDashboardMetrics(timeRange = '30d'): Promise<AxiosResponse<DashboardMetrics>> {
    return this.axios.get('/admin/analytics/dashboard', { params: { timeRange } });
  }

  getUsageTimeSeries(timeRange = '30d'): Promise<AxiosResponse<UsageTimeSeriesResponse>> {
    return this.axios.get('/admin/analytics/usage', { params: { timeRange } });
  }

  getAgentAnalytics(timeRange = '7d'): Promise<AxiosResponse<AgentAnalyticsResponse>> {
    return this.axios.get('/admin/analytics/agents', { params: { timeRange } });
  }

  getToolUsage(params: GetToolUsageParams = {}): Promise<AxiosResponse<ToolUsageAnalyticsResponse>> {
    return this.axios.get('/admin/analytics/tools', { params: { timeRange: '30d', ...params } });
  }

  getTraces(params: GetTracesParams = {}): Promise<AxiosResponse<TracesResponse>> {
    return this.axios.get('/admin/analytics/traces', { params });
  }

  getTraceDetail(traceId: string, organizationId?: string): Promise<AxiosResponse<TraceDetailResponse>> {
    return this.axios.get(`/admin/analytics/traces/${traceId}`, { params: { organizationId } });
  }

  getSpanDetail(traceId: string, spanId: string, organizationId?: string): Promise<AxiosResponse<SpanDetail>> {
    return this.axios.get(`/admin/analytics/traces/${traceId}/spans/${spanId}`, { params: { organizationId } });
  }

  getTraceScorers(traceId: string): Promise<AxiosResponse<ScorerResultsResponse>> {
    return this.axios.get(`/admin/analytics/traces/${traceId}/scorers`);
  }

  getScorerSummary(timeRange = '30d', agentName?: string): Promise<AxiosResponse<ScorerSummaryResponse>> {
    return this.axios.get('/admin/analytics/scorers', { params: { timeRange, agentName } });
  }

  getScorerResults(params: GetScorerResultsParams = {}): Promise<AxiosResponse<ScorerResultListResponse>> {
    return this.axios.get('/admin/analytics/scorer-results', { params });
  }

  getScorerTimeSeries(
    params: { timeRange?: string; scorerId?: string; agentName?: string } = {},
  ): Promise<AxiosResponse<ScorerTimeSeriesResponse>> {
    return this.axios.get('/admin/analytics/scorer-timeseries', { params });
  }

  getScorerCalibration(): Promise<AxiosResponse<ScorerCalibrationResponse>> {
    return this.axios.get('/admin/analytics/scorer-calibration');
  }

  getScorerCorrelation(timeRange = '30d'): Promise<AxiosResponse<ScorerCorrelationResponse>> {
    return this.axios.get('/admin/analytics/scorer-correlation', { params: { timeRange } });
  }

  getOrgUsers(): Promise<AxiosResponse<OrgUsersResponse>> {
    return this.axios.get('/admin/users');
  }

  getAiUsers(params: GetAiUsersParams = {}): Promise<AxiosResponse<AiUserSummaryResponse>> {
    return this.axios.get('/admin/analytics/ai-users', { params });
  }

  getAiUserDetail(userId: string, timeRange = '30d'): Promise<AxiosResponse<AiUserDetail>> {
    return this.axios.get(`/admin/analytics/ai-users/${userId}`, { params: { timeRange } });
  }

  getExportData(timeRange = '30d'): Promise<AxiosResponse<ExportDataResponse>> {
    return this.axios.get('/admin/analytics/export', { params: { timeRange } });
  }

  getFeedbackList(params: GetFeedbackParams = {}): Promise<AxiosResponse<ChatFeedbackListResponse>> {
    return this.axios.get('/admin/analytics/feedback', { params });
  }

  getFeedbackDetail(feedbackId: string, organizationId?: string): Promise<AxiosResponse<ChatFeedbackDetail>> {
    return this.axios.get(`/admin/analytics/feedback/${feedbackId}`, { params: { organizationId } });
  }

  getMcpFeedbackList(params: GetMcpFeedbackParams = {}): Promise<AxiosResponse<McpFeedbackListResponse>> {
    return this.axios.get('/admin/analytics/mcp-feedback', { params });
  }

  getCopilotPerformance(timeRange = '30d', agentName?: string): Promise<AxiosResponse<CopilotPerformanceMetrics>> {
    return this.axios.get('/admin/analytics/copilot-performance', { params: { timeRange, agentName } });
  }

  getCopilotPerformanceTimeSeries(
    timeRange = '30d',
    agentName?: string,
  ): Promise<AxiosResponse<CopilotPerformanceTimeSeriesResponse>> {
    return this.axios.get('/admin/analytics/copilot-performance/timeseries', {
      params: { timeRange, agentName },
    });
  }

  getCopilotPerformanceByScope(
    timeRange = '30d',
    agentName?: string,
  ): Promise<AxiosResponse<CopilotPerformanceByScopeResponse>> {
    return this.axios.get('/admin/analytics/copilot-performance/by-scope', {
      params: { timeRange, agentName },
    });
  }

  getCopilotPerformanceSteps(
    timeRange = '30d',
    agentName?: string,
  ): Promise<AxiosResponse<CopilotPerformanceStepsResponse>> {
    return this.axios.get('/admin/analytics/copilot-performance/steps', {
      params: { timeRange, agentName },
    });
  }

  getCopilotDbQueries(
    timeRange = '30d',
    limit?: number,
    agentName?: string,
  ): Promise<AxiosResponse<CopilotDbQueriesResponse>> {
    return this.axios.get('/admin/analytics/copilot-performance/db-queries', {
      params: { timeRange, limit, agentName },
    });
  }

  getCostBreakdown(timeRange = '30d'): Promise<AxiosResponse<CostBreakdownResponse>> {
    return this.axios.get('/admin/analytics/costs', { params: { timeRange } });
  }

  getCacheMetrics(): Promise<AxiosResponse<CacheMetrics>> {
    return this.axios.get('/admin/analytics/cache');
  }

  clearCache(): Promise<AxiosResponse<CacheClearResult>> {
    return this.axios.delete('/admin/analytics/cache');
  }

  // Skills browser
  listSkillBranches(): Promise<AxiosResponse<BranchListResponse>> {
    return this.axios.get('/admin/skills/branches');
  }

  getSkillCatalog(ref: string): Promise<AxiosResponse<SkillCatalogResponse>> {
    return this.axios.get('/admin/skills/catalog', { params: { ref } });
  }

  getSkillDetail(name: string, ref: string): Promise<AxiosResponse<SkillDetailResponse>> {
    return this.axios.get(`/admin/skills/${encodeURIComponent(name)}`, { params: { ref } });
  }

  getSkillDiff(ref: string, base = 'main'): Promise<AxiosResponse<SkillDiffResponse>> {
    return this.axios.get('/admin/skills/diff', { params: { ref, base } });
  }

  // Skill source override
  getSkillSource(): Promise<AxiosResponse<SkillSourceResponse>> {
    return this.axios.get('/admin/skill-sources');
  }

  setSkillSource(ref: string): Promise<AxiosResponse<SkillSourceResponse>> {
    return this.axios.put('/admin/skill-sources', { ref });
  }

  clearSkillSource(): Promise<AxiosResponse<void>> {
    return this.axios.delete('/admin/skill-sources');
  }

  refreshSkillSource(): Promise<AxiosResponse<void>> {
    return this.axios.post('/admin/skill-sources/refresh');
  }
}
